import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest, HttpMethod } from '@api/common';
import { queryKeys, showApiError } from '@api/queryClient';
import toastService from '@services/toastService';

// ============================================================================
// Types
// ============================================================================

export interface DeliveryDestination {
  id: string | null;
  customerId: string | null;
  customerName: string | null;
  name: string;
  name2: string;
  code: string;
  reference: string;
  notes: string;
  streetAddress: string;
  postalCode: string;
  city: string;
  country: string;
  created: string | null;
  modified?: string | null;
}

// ============================================================================
// API Functions
// ============================================================================

const API_ENDPOINT = 'deliveryDestinations';

const fetchDeliveryDestinations = async (): Promise<DeliveryDestination[]> => {
  return apiRequest<void, DeliveryDestination[]>(HttpMethod.GET, API_ENDPOINT);
};

const createDeliveryDestination = async (destination: DeliveryDestination): Promise<DeliveryDestination> => {
  return apiRequest<DeliveryDestination, DeliveryDestination>(HttpMethod.POST, API_ENDPOINT, destination);
};

const updateDeliveryDestination = async (destination: DeliveryDestination): Promise<DeliveryDestination> => {
  return apiRequest<DeliveryDestination, DeliveryDestination>(
    HttpMethod.PUT,
    `${API_ENDPOINT}/${destination.id}`,
    destination
  );
};

const deleteDeliveryDestination = async (destination: DeliveryDestination): Promise<void> => {
  return apiRequest<string, void>(HttpMethod.DELETE, `${API_ENDPOINT}/${destination.id}`, destination.id);
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch all delivery destinations
 */
export const useDeliveryDestinationsQuery = () => {
  return useQuery({
    queryKey: queryKeys.deliveryDestinations.all,
    queryFn: fetchDeliveryDestinations,
  });
};

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Create a new delivery destination
 */
export const useCreateDeliveryDestinationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createDeliveryDestination,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.deliveryDestinations.all });
      toastService.success({ title: 'save_succeeded' });
    },
    onError: (error: unknown) => {
      showApiError(error, 'save_failed');
    },
  });
};

/**
 * Update an existing delivery destination
 */
export const useUpdateDeliveryDestinationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateDeliveryDestination,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.deliveryDestinations.all });
      toastService.success({ title: 'save_succeeded' });
    },
    onError: (error: unknown) => {
      showApiError(error, 'save_failed');
    },
  });
};

/**
 * Delete a delivery destination
 */
export const useDeleteDeliveryDestinationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteDeliveryDestination,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.deliveryDestinations.all });
      toastService.success({ title: 'deliveryDestinations.deliveryDestination_removed' });
    },
    onError: (error: unknown) => {
      showApiError(error, 'save_failed');
    },
  });
};
